/**
 * Dynamic policy builder (epic-6 §6 / PB-1).
 *
 * Used by:
 *  - PoliciesPage "New/Edit" (statements + policy-level panels)
 *  - RolesPage policySpecs editor (statements only — `showPolicyLevel={false}`)
 *  - /policies/builder full-page route (PolicyBuilderPage)
 *
 * The builder is a CONTROLLED component: the parent owns the draft and calls
 * `onChange` with the next draft. It never talks to the backend on its own
 * except for the explicit Validate-expression / Evaluate-test actions.
 */
import { useMemo, useState } from 'react';
import {
  Plus,
  Trash2,
  ShieldCheck,
  ShieldX,
  ChevronDown,
  Hammer,
  FlaskConical,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { useToast } from '@/components/shared/Toast';
import { MultiSelect, type MultiSelectOption } from '@/components/agents/MultiSelect';
import {
  useGetRegistryServicesQuery,
  useValidatePolicyExpressionMutation,
  useEvaluatePolicyRequestMutation,
  useGetPolicyOptionsQuery,
} from '@/store/api/policyBuilderApi';
import { useGetProjectsQuery } from '@/store/api/projectsApi';
import type {
  PolicyStatement,
  PolicyEffect,
  PolicyRateLimit,
  PolicySummary,
  PolicyExpressionValidation,
  PolicyTemplate,
} from '@/types';

export interface PolicyBuilderValue {
  name?: string;
  description?: string;
  statements: PolicyStatement[];
  priority?: number;
  parentPolicyId?: string;
  effectiveAt?: string;
  ttlSeconds?: number;
  rateLimit?: PolicyRateLimit;
}

interface PolicyBuilderProps {
  value: PolicyBuilderValue;
  onChange: (next: PolicyBuilderValue) => void;
  /** Show the policy-level panels (rate limits, validity, priority, parent). */
  showPolicyLevel?: boolean;
  /** Show the template gallery (default true in full mode). */
  showTemplates?: boolean;
  /** External parent-policy options (fetched internally when omitted). */
  policies?: PolicySummary[];
  submitLabel?: string;
  onSubmit?: (value: PolicyBuilderValue) => void | Promise<void>;
  submitting?: boolean;
}

// ─── Pure helpers (unit-tested in src/components/policies/__tests__) ──────────

export const DEFAULT_TTLS = [
  { label: '5 minutes', seconds: 300 },
  { label: '1 hour', seconds: 3600 },
  { label: '24 hours', seconds: 86400 },
];

export function createDefaultStatement(): PolicyStatement {
  return { effect: 'Allow', actions: [], resources: [], conditions: [] };
}

/**
 * Returns the 0-based index of the first statement whose `actions` or
 * `resources` is missing/empty, or null when every statement is complete.
 *
 * The authorization backplane requires at least one action AND one resource per
 * statement (`@ArrayMinSize(1)`, policy.dto.ts), so an incomplete statement can
 * never be persisted — callers must block save with a clear error instead of
 * letting the backend fail later on assign.
 */
export function findIncompleteStatement(
  statements: ReadonlyArray<Record<string, unknown> | PolicyStatement>,
): number | null {
  for (let i = 0; i < statements.length; i++) {
    const stmt = statements[i] ?? {};
    const actions = Array.isArray(stmt.actions) ? stmt.actions : [];
    const resources = Array.isArray(stmt.resources) ? stmt.resources : [];
    if (actions.length === 0 || resources.length === 0) {
      return i;
    }
  }
  return null;
}

/** Split a free-text comma/space separated tag input into unique non-empty tags. */
export function splitTagInput(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[,\n]/)
        .map((t) => t.trim())
        .filter(Boolean),
    ),
  ];
}

/** TTL values > 1h require the `authorization:policy:extend-expiry` permission. */
export function requiresExtendExpiry(ttlSeconds?: number): boolean {
  return typeof ttlSeconds === 'number' && ttlSeconds > 3600;
}

export const POLICY_TEMPLATES: PolicyTemplate[] = [
  {
    id: 'storage-read-collection',
    name: 'Storage read (collection)',
    description: 'Read files from any storage collection.',
    policy: {
      name: 'storage-read-collection',
      statements: [{ effect: 'Allow', actions: ['storage:files:read', 'storage:files:download'], resources: ['storage:collections:*', 'storage:files:*'] }],
    },
  },
  {
    id: 'agent-dispatch',
    name: 'Agent dispatch',
    description: 'Create and drive agent conversations.',
    policy: {
      name: 'agent-dispatch',
      statements: [{ effect: 'Allow', actions: ['agent-runtime:conversations:create', 'agent-runtime:conversations:send', 'agent-runtime:conversations:read'], resources: ['agent-runtime:conversations:*'] }],
      rateLimit: { requestsPerMinute: 20 },
    },
  },
  {
    id: 'invoke-service',
    name: 'Invoke service',
    description: 'Allow calling any registered service through the core proxy.',
    policy: {
      name: 'invoke-service',
      statements: [{ effect: 'Allow', actions: ['invoke:*'], resources: ['*'] }],
      priority: 0,
    },
  },
  {
    id: 'approvals-decide',
    name: 'Approvals decide',
    description: 'Approve or reject bulk/conversation approval requests.',
    policy: {
      name: 'approvals-decide',
      statements: [{ effect: 'Allow', actions: ['approvals:decide'], resources: ['approvals:*'] }],
    },
  },
];

// ─── Tags editor w/ autocomplete ─────────────────────────────────────────────

interface TagEditorProps {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
}

function TagEditor({ label, value, onChange, placeholder, suggestions }: TagEditorProps) {
  const [text, setText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  const add = (raw: string) => {
    const next = splitTagInput(raw);
    if (next.length > 0) {
      onChange([...new Set([...value, ...next])]);
    }
    setText('');
  };

  const filtered = useMemo(() => {
    if (!suggestions || text.trim().length === 0) return [];
    const q = text.trim().toLowerCase();
    return suggestions.filter((s) => s.toLowerCase().includes(q)).slice(0, 8);
  }, [suggestions, text]);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1 rounded-md border p-2">
        {value.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-1 font-mono text-[11px]">
            {tag}
            <button
              type="button"
              className="ml-0.5 text-muted-foreground hover:text-destructive"
              onClick={() => onChange(value.filter((t) => t !== tag))}
            >
              ×
            </button>
          </Badge>
        ))}
        <div className="relative min-w-[160px] flex-1">
          <input
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            value={text}
            placeholder={placeholder ?? 'type + Enter to add'}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setShowSuggestions(false)}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                add(text);
              } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
                onChange(value.slice(0, -1));
              }
            }}
          />
          {showSuggestions && filtered.length > 0 && (
            <div className="absolute left-0 top-full z-20 mt-1 max-h-48 w-72 overflow-auto rounded-md border bg-popover p-1 shadow-md">
              {filtered.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="block w-full rounded px-2 py-1 text-left font-mono text-xs hover:bg-accent"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    add(s);
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Condition rows w/ JSEP validation ───────────────────────────────────────

interface ConditionRow {
  expression: string;
  description?: string;
}

interface ConditionsEditorProps {
  conditions: ConditionRow[];
  onChange: (next: ConditionRow[]) => void;
}

function ConditionsEditor({ conditions, onChange }: ConditionsEditorProps) {
  const [validateExpression, { isLoading }] = useValidatePolicyExpressionMutation();
  const [resultByRow, setResultByRow] = useState<Record<number, PolicyExpressionValidation | undefined>>({});

  const update = (index: number, field: keyof ConditionRow, val: string) => {
    const next = [...conditions];
    next[index] = { ...next[index], [field]: val };
    onChange(next);
    // Invalidate cached validation for edited rows.
    setResultByRow((prev) => ({ ...prev, [index]: undefined }));
  };

  const runValidate = async (index: number) => {
    const expression = conditions[index]?.expression;
    if (!expression || expression.trim().length === 0) return;
    try {
      const res = await validateExpression({ expression }).unwrap();
      setResultByRow((prev) => ({ ...prev, [index]: res }));
    } catch {
      setResultByRow((prev) => ({ ...prev, [index]: { valid: false, error: 'Validation request failed' } }));
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">Conditions (JSEP expression)</Label>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onChange([...conditions, { expression: '' }])}
        >
          <Plus className="mr-1 h-3 w-3" /> Add condition
        </Button>
      </div>
      {conditions.length === 0 && (
        <p className="text-xs text-muted-foreground">No conditions — statement applies unconditionally.</p>
      )}
      {conditions.map((cond, i) => (
        <div key={i} className="rounded-md border p-2 space-y-1.5">
          <div className="flex items-start gap-2">
            <Input
              className="font-mono text-xs"
              placeholder={'["eq", ["get", "ip"], "10.0.0.0/8"]'}
              value={cond.expression}
              onChange={(e) => update(i, 'expression', e.target.value)}
            />
            <Button variant="outline" size="sm" className="h-9 shrink-0 text-xs" onClick={() => runValidate(i)} disabled={isLoading}>
              <ShieldCheck className="mr-1 h-3 w-3" />
              Validate
            </Button>
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => onChange(conditions.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
          <Input
            placeholder="Description (optional)"
            className="text-xs"
            value={cond.description ?? ''}
            onChange={(e) => update(i, 'description', e.target.value)}
          />
          {resultByRow[i] && (
            <p className={`text-xs ${resultByRow[i].valid ? 'text-green-600' : 'text-destructive'}`}>
              {resultByRow[i].valid
                ? `Valid expression — referenced fields: ${resultByRow[i].referencedFields?.join(', ') ?? 'none'}`
                : `Invalid: ${resultByRow[i].error ?? 'syntax error'}`}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Statement card ───────────────────────────────────────────────────────────

const RESOURCE_HELPERS: MultiSelectOption[] = [
  { value: 'storage:collections:*', label: 'storage:collections:*' },
  { value: 'storage:files:*', label: 'storage:files:*' },
  { value: 'meetings:*', label: 'meetings:*' },
  { value: 'planning:tasks:*', label: 'planning:tasks:*' },
  { value: 'repositories:*', label: 'repositories:*' },
  { value: 'agent-factory:*', label: 'agent-factory:*' },
  { value: 'agent-runtime:conversations:*', label: 'agent-runtime:conversations:*' },
  { value: 'approvals:*', label: 'approvals:*' },
  { value: 'voice:*', label: 'voice:*' },
  { value: 'daytona:sandboxes:*', label: 'daytona:sandboxes:*' },
  { value: '*', label: '* (all)' },
];

interface StatementCardProps {
  index: number;
  statement: PolicyStatement;
  total: number;
  actionSuggestions: string[];
  projects: MultiSelectOption[];
  onUpdate: (next: PolicyStatement) => void;
  onRemove: () => void;
}

function StatementCard({ index, statement, total, actionSuggestions, projects, onUpdate, onRemove }: StatementCardProps) {
  const [showHelpers, setShowHelpers] = useState(false);

  return (
    <Card key={index} className="border-muted">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 py-2.5 px-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="h-6 w-6 justify-center rounded-full p-0 font-mono text-xs">
            {index + 1}
          </Badge>
          <CardTitle className="text-sm">Statement</CardTitle>
        </div>
        {total > 1 && (
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onRemove} title="Remove statement">
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3 px-3 pb-3">
        <div className="flex gap-2">
          <Button
            type="button"
            variant={statement.effect === 'Allow' ? 'default' : 'outline'}
            size="sm"
            className={statement.effect === 'Allow' ? 'bg-green-700 hover:bg-green-800' : ''}
            onClick={() => onUpdate({ ...statement, effect: 'Allow' })}
          >
            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Allow
          </Button>
          <Button
            type="button"
            variant={statement.effect === 'Deny' ? 'default' : 'outline'}
            size="sm"
            className={statement.effect === 'Deny' ? 'bg-red-700 hover:bg-red-800' : ''}
            onClick={() => onUpdate({ ...statement, effect: 'Deny' })}
          >
            <ShieldX className="mr-1 h-3.5 w-3.5" /> Deny
          </Button>
        </div>

        <TagEditor
          label="Actions"
          placeholder="invoke:<service>:<method>"
          value={statement.actions}
          onChange={(actions) => onUpdate({ ...statement, actions })}
          suggestions={actionSuggestions}
        />

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-xs">Resources</Label>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => setShowHelpers((v) => !v)}
            >
              <ChevronDown className={`mr-1 h-3 w-3 transition-transform ${showHelpers ? 'rotate-180' : ''}`} />
              Helpers
            </Button>
          </div>
          {showHelpers && (
            <div className="space-y-2 rounded-md border p-2">
              <MultiSelect
                label="Preset resource patterns"
                options={RESOURCE_HELPERS}
                value={statement.resources.filter((r) => RESOURCE_HELPERS.some((h) => h.value === r))}
                onChange={(selected) => {
                  const nonPreset = statement.resources.filter((r) => !RESOURCE_HELPERS.some((h) => h.value === r));
                  onUpdate({ ...statement, resources: [...nonPreset, ...selected] });
                }}
                placeholder="Pick preset patterns"
              />
              {projects.length > 0 && (
                <MultiSelect
                  label={`project:<id>`}
                  options={projects}
                  value={statement.resources.filter((r) => r.startsWith('project:'))}
                  onChange={(selected) => {
                    const nonProject = statement.resources.filter((r) => !r.startsWith('project:'));
                    onUpdate({ ...statement, resources: [...nonProject, ...selected] });
                  }}
                  placeholder="Attach project resources"
                />
              )}
            </div>
          )}
          <TagEditor
            label=""
            placeholder={'storage:files:* , project:<id> , org:<id>'}
            value={statement.resources.filter((r) => !RESOURCE_HELPERS.some((h) => h.value === r))}
            onChange={(raw) => {
              const preset = statement.resources.filter((r) => RESOURCE_HELPERS.some((h) => h.value === r));
              onUpdate({ ...statement, resources: [...preset, ...raw] });
            }}
          />
        </div>

        <ConditionsEditor
          conditions={(statement.conditions ?? []).map((c) => ({
            expression: c.expression ?? '',
            description: c.description ?? '',
          }))}
          onChange={(conditions) => onUpdate({ ...statement, conditions: conditions as PolicyStatement['conditions'] })}
        />
      </CardContent>
    </Card>
  );
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function PolicyBuilder({
  value,
  onChange,
  showPolicyLevel = true,
  showTemplates = true,
  policies,
  submitLabel = 'Save Policy',
  onSubmit,
  submitting = false,
}: PolicyBuilderProps) {
  const toast = useToast();
  const { data: registryServices } = useGetRegistryServicesQuery();
  const { data: policyOptions } = useGetPolicyOptionsQuery(undefined, { skip: showPolicyLevel && !!policies });
  const { data: projectOptionsData } = useGetProjectsQuery();
  const [evaluateRequest, { data: evalResult, isLoading: isEvaluating }] = useEvaluatePolicyRequestMutation();
  const [evalInputs, setEvalInputs] = useState({
    userId: '',
    action: '',
    resource: '',
    context: '{}',
  });
  const [ttlMode, setTtlMode] = useState<'5m' | '1h' | '24h' | 'custom'>('1h');

  const actionSuggestions = useMemo(() => {
    const fromMethods = new Set<string>();
    for (const svc of registryServices ?? []) {
      const name = svc.name;
      for (const ep of svc.endpoints ?? []) {
        fromMethods.add(`invoke:${name}:${ep.method === 'ALL' ? '*' : ep.method.toLowerCase()}`);
      }
      if ((svc.endpoints ?? []).length === 0) {
        fromMethods.add(`invoke:${name}:*`);
      }
      fromMethods.add(`invoke:${name}:*`);
    }
    // Static action catalogs (storage / agent core) — fallback when registry is down.
    const staticActions = [
      'storage:files:read', 'storage:files:upload', 'storage:files:delete', 'storage:files:download',
      'storage:collections:create', 'storage:collections:read', 'storage:collections:delete',
      'agent-runtime:conversations:create', 'agent-runtime:conversations:read', 'agent-runtime:conversations:send',
      'agent-factory:factories:create', 'agent-factory:factories:read', 'agent-factory:factories:dispatch',
      'meetings:create', 'meetings:read', 'meetings:join', 'meetings:end',
      'planning:tasks:create', 'planning:tasks:read', 'planning:tasks:update', 'planning:tasks:delete',
      'repositories:read', 'repositories:execute', 'approvals:decide', 'voice:synthesize',
      'indexer:search', 'indexer:read',
    ];
    return [...new Set([...fromMethods, ...staticActions])].sort();
  }, [registryServices]);

  const projectOptions: MultiSelectOption[] = useMemo(
    () => (projectOptionsData ?? []).map((p) => ({ value: `project:${p._id}`, label: `project:${p.name ?? p._id}` })),
    [projectOptionsData],
  );

  const parentOptions: PolicySummary[] = policies ?? policyOptions ?? [];

  const setStatements = (statements: PolicyStatement[]) => onChange({ ...value, statements });

  const updateStatement = (index: number, next: PolicyStatement) => {
    const statements = [...value.statements];
    statements[index] = next;
    setStatements(statements);
  };

  const setTtl = (mode: '5m' | '1h' | '24h' | 'custom', customSeconds?: number) => {
    setTtlMode(mode);
    const seconds = mode === '5m' ? 300 : mode === '1h' ? 3600 : mode === '24h' ? 86400 : customSeconds ?? 3600;
    onChange({ ...value, ttlSeconds: seconds });
  };

  const applyTemplate = (template: PolicyTemplate) => {
    onChange({
      ...value,
      name: template.policy.name,
      statements: template.policy.statements.map((s) => ({
        ...s,
        actions: [...s.actions],
        resources: [...s.resources],
        conditions: s.conditions ?? [],
      })),
      priority: template.policy.priority ?? value.priority,
      rateLimit: template.policy.rateLimit ?? value.rateLimit,
      ttlSeconds: template.policy.ttlSeconds ?? value.ttlSeconds,
    });
    toast.info(`Template "${template.name}" applied`);
  };

  const runEvaluate = async () => {
    let context: Record<string, unknown> | undefined;
    try {
      context = evalInputs.context.trim() ? JSON.parse(evalInputs.context) : undefined;
    } catch {
      toast.error('Context must be valid JSON (or leave empty)');
      return;
    }
    if (!evalInputs.userId.trim() || !evalInputs.action.trim() || !evalInputs.resource.trim()) {
      toast.warning('Evaluate needs userId, action and resource');
      return;
    }
    try {
      await evaluateRequest({
        userId: evalInputs.userId.trim(),
        action: evalInputs.action.trim(),
        resource: evalInputs.resource.trim(),
        context,
        policyIds: undefined,
      }).unwrap();
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Evaluation failed');
    }
  };

  const handleSubmit = () => {
    if (!onSubmit) return;
    // Airtight save guard: every statement must define >=1 action and >=1
    // resource (the authorization backplane rejects empty collections with 400).
    const incomplete = findIncompleteStatement(value.statements);
    if (incomplete !== null) {
      toast.error(`Statement ${incomplete + 1} must define at least 1 action and 1 resource`);
      return;
    }
    void onSubmit(value);
  };

  return (
    <div className="space-y-4">
      {(showTemplates || showPolicyLevel) && (
        <div className="grid gap-3 lg:grid-cols-2">
          {showTemplates && (
            <Card>
              <CardHeader className="py-2.5 px-3">
                <CardTitle className="flex items-center gap-1.5 text-sm">
                  <Hammer className="h-4 w-4" /> Templates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 pb-3">
                {POLICY_TEMPLATES.map((tpl) => (
                  <Button key={tpl.id} variant="outline" size="sm" className="h-auto w-full justify-start py-1.5 text-left" onClick={() => applyTemplate(tpl)}>
                    <span className="text-xs font-medium">{tpl.name}</span>
                    <span className="ml-2 truncate text-[11px] text-muted-foreground">{tpl.description}</span>
                  </Button>
                ))}
              </CardContent>
            </Card>
          )}

          {showPolicyLevel && (
            <Card className="space-y-3">
              <CardHeader className="py-2.5 px-3">
                <CardTitle className="text-sm">Policy-level settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 px-3 pb-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Rate limit (per minute)</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="unlimited"
                      value={value.rateLimit?.requestsPerMinute ?? ''}
                      onChange={(e) =>
                        onChange({
                          ...value,
                          rateLimit: {
                            ...value.rateLimit,
                            requestsPerMinute: e.target.value === '' ? undefined : Number(e.target.value),
                          },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rate limit (per hour)</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="unlimited"
                      value={value.rateLimit?.requestsPerHour ?? ''}
                      onChange={(e) =>
                        onChange({
                          ...value,
                          rateLimit: {
                            ...value.rateLimit,
                            requestsPerHour: e.target.value === '' ? undefined : Number(e.target.value),
                          },
                        })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Effective at</Label>
                  <Input
                    type="datetime-local"
                    value={value.effectiveAt ? toLocalInputValue(value.effectiveAt) : ''}
                    onChange={(e) =>
                      onChange({ ...value, effectiveAt: e.target.value ? new Date(e.target.value).toISOString() : undefined })
                    }
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">TTL</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {DEFAULT_TTLS.map((t) => (
                      <Button
                        key={t.seconds}
                        type="button"
                        variant={ttlMode === (t.seconds === 300 ? '5m' : t.seconds === 3600 ? '1h' : '24h') ? 'default' : 'outline'}
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => setTtl(t.seconds === 300 ? '5m' : t.seconds === 3600 ? '1h' : '24h')}
                      >
                        {t.label}
                      </Button>
                    ))}
                    <Button
                      type="button"
                      variant={ttlMode === 'custom' ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setTtl('custom', (value.ttlSeconds ?? 3600) > 3600 && value.ttlSeconds !== 86400 && value.ttlSeconds !== 300 && value.ttlSeconds !== 3600 ? value.ttlSeconds! : 7200)}
                    >
                      Custom
                    </Button>
                  </div>
                  {ttlMode === 'custom' && (
                    <Input
                      type="number"
                      min={60}
                      placeholder="seconds"
                      value={value.ttlSeconds ?? 7200}
                      onChange={(e) => setTtl('custom', Number(e.target.value) || 7200)}
                    />
                  )}
                  {requiresExtendExpiry(value.ttlSeconds) && (
                    <p className="text-xs text-amber-600">
                      TTL &gt; 1 hour requires the <code>authorization:policy:extend-expiry</code> permission.
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Priority (higher wins, deny overrides)</Label>
                  <Input
                    type="number"
                    value={value.priority ?? 0}
                    onChange={(e) => onChange({ ...value, priority: Number(e.target.value) || 0 })}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Parent policy (policy cascade)</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    value={value.parentPolicyId ?? ''}
                    onChange={(e) => onChange({ ...value, parentPolicyId: e.target.value || undefined })}
                  >
                    <option value="">— none —</option>
                    {parentOptions.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                        {p.isActive === false ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Statements */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Statements</Label>
          <Button variant="outline" size="sm" onClick={() => setStatements([...value.statements, createDefaultStatement()])}>
            <Plus className="mr-1 h-3 w-3" /> Add Statement
          </Button>
        </div>
        {value.statements.length === 0 && (
          <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
            No statements. Click "Add Statement" to define the first allow/deny rule.
          </p>
        )}
        {value.statements.map((stmt, i) => (
          <StatementCard
            key={i}
            index={i}
            statement={stmt}
            total={value.statements.length}
            actionSuggestions={actionSuggestions}
            projects={projectOptions}
            onUpdate={(next) => updateStatement(i, next)}
            onRemove={() => setStatements(value.statements.filter((_, idx) => idx !== i))}
          />
        ))}
      </div>

      {/* Evaluate test panel */}
      <Card>
        <CardHeader className="py-2.5 px-3">
          <CardTitle className="flex items-center gap-1.5 text-sm">
            <FlaskConical className="h-4 w-4" /> Evaluate test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-3 pb-3">
          <p className="text-xs text-muted-foreground">
            Live feedback loop against POST /policies/evaluate — checks the draft as a real user request.
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">User ID</Label>
              <Input placeholder="user-123" value={evalInputs.userId} onChange={(e) => setEvalInputs({ ...evalInputs, userId: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Action</Label>
              <Input placeholder="invoke:storage:get" value={evalInputs.action} onChange={(e) => setEvalInputs({ ...evalInputs, action: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Resource</Label>
              <Input placeholder="storage:files:*" value={evalInputs.resource} onChange={(e) => setEvalInputs({ ...evalInputs, resource: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Context (JSON, optional)</Label>
            <Input
              className="font-mono text-xs"
              placeholder='{"ip": "1.2.3.4", "time": "08:00"}'
              value={evalInputs.context}
              onChange={(e) => setEvalInputs({ ...evalInputs, context: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={runEvaluate} disabled={isEvaluating}>
              {isEvaluating ? 'Evaluating…' : 'Run evaluation'}
            </Button>
            {evalResult && (
              <div className="flex items-center gap-2">
                <Badge variant={evalResult.allowed ? 'default' : 'destructive'}>
                  {evalResult.allowed ? 'ALLOWED' : 'DENIED'}
                </Badge>
                {evalResult.rateLimit && (
                  <span className="text-xs text-muted-foreground">
                    rateLimit: {evalResult.rateLimit.requestsPerMinute ? `${evalResult.rateLimit.requestsPerMinute}/min` : ''}
                    {evalResult.rateLimit.requestsPerHour ? `${evalResult.rateLimit.requestsPerHour}/h` : ''}
                  </span>
                )}
              </div>
            )}
          </div>
          {evalResult && (
            <JsonViewer data={evalResult} />
          )}
        </CardContent>
      </Card>

      {onSubmit && (
        <div className="flex justify-end">
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toLocalInputValue(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}